// The validator queue (GET /practice-submissions): learner · session ·
// practice · submitted · evidence · Validate / Reject with an optional score
// and note. The API narrows rows to the caller's reach (manage: all; assign:
// their people) and decides who may validate which verification kind — a
// refusal surfaces as the server's message. Filters: status, session.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { getApiErrorMessage } from '@/lib/apiError';
import type { Submission, SubmissionStatus, ValidateInput } from '@/services/learningService';
import { useSubmissions, useValidateSubmission } from '../api/hooks';
import { SUBMISSION_STATUS_CLS, submissionStatusLabel, verificationLabel } from '../lib/completion';

const selectCls = 'h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground';
const STATUSES: SubmissionStatus[] = ['submitted', 'pending', 'validated', 'rejected'];

interface Props {
  /** Pin the queue to one session (hides the session filter). */
  sessionId?: string;
  /** Session options for the filter when not pinned. */
  sessionOptions?: Array<{ id: string; title: string }>;
  defaultStatus?: SubmissionStatus | '';
  /** Hide the status filter (e.g. the manager card shows submitted only). */
  lockStatus?: boolean;
  className?: string;
}

export const ValidationQueue: React.FC<Props> = ({ sessionId, sessionOptions, defaultStatus = 'submitted', lockStatus, className }) => {
  const t = useT();
  const [status, setStatus] = useState<SubmissionStatus | ''>(defaultStatus);
  const [sessionFilter, setSessionFilter] = useState('');
  const sid = sessionId || sessionFilter || undefined;
  const q = useSubmissions({ session_id: sid, status });
  const validate = useValidateSubmission();
  const [decide, setDecide] = useState<{ id: string; decision: ValidateInput['decision']; score: string; note: string } | null>(null);

  const rows = q.data || [];
  const send = () => {
    if (!decide) return;
    const body: ValidateInput = { decision: decide.decision };
    if (decide.score.trim() !== '') body.score = Number(decide.score);
    if (decide.note.trim()) body.note = decide.note.trim();
    validate.mutate({ submissionId: decide.id, body }, {
      onSuccess: () => { toast.success(decide.decision === 'validated' ? t('training:validated_ok', 'Validated') : t('training:rejected_ok', 'Rejected')); setDecide(null); },
    });
  };

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="validation-queue">
      {(!lockStatus || (!sessionId && (sessionOptions || []).length > 0)) && (
        <div className="flex flex-wrap items-end gap-2">
          {!lockStatus && (
            <div className="space-y-1">
              <Label htmlFor="vq-status" className="text-xs">{t('training:col_status', 'Status')}</Label>
              <select id="vq-status" className={selectCls} value={status} onChange={(e) => setStatus(e.target.value as SubmissionStatus | '')}>
                <option value="">{t('training:all', 'All')}</option>
                {STATUSES.map((s) => <option key={s} value={s}>{submissionStatusLabel(t, s)}</option>)}
              </select>
            </div>
          )}
          {!sessionId && (sessionOptions || []).length > 0 && (
            <div className="space-y-1">
              <Label htmlFor="vq-session" className="text-xs">{t('training:pick_session', 'Session')}</Label>
              <select id="vq-session" className={selectCls} value={sessionFilter} onChange={(e) => setSessionFilter(e.target.value)}>
                <option value="">{t('training:all', 'All')}</option>
                {(sessionOptions || []).map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
          )}
        </div>
      )}
      {q.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
      {q.isError && <p className="text-xs text-red-600 dark:text-red-400">{getApiErrorMessage(q.error, t('training:err', 'Something went wrong'))}</p>}
      {!q.isLoading && !q.isError && rows.length === 0 && <p className="text-sm text-muted-foreground">{t('training:no_submissions', 'Nothing to validate.')}</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground text-xs">
              <tr>
                <th className="text-left font-medium px-2.5 py-2">{t('training:col_learner', 'Learner')}</th>
                <th className="text-left font-medium px-2.5 py-2">{t('training:pick_session', 'Session')}</th>
                <th className="text-left font-medium px-2.5 py-2">{t('training:practice_title', 'Practical exercise')}</th>
                <th className="text-left font-medium px-2.5 py-2">{t('training:col_submitted', 'Submitted')}</th>
                <th className="text-left font-medium px-2.5 py-2">{t('training:col_evidence', 'Evidence')}</th>
                <th className="px-2.5 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((s: Submission) => (
                <React.Fragment key={s.id}>
                  <tr data-testid={`sub-row-${s.id}`}>
                    <td className="px-2.5 py-1.5 text-foreground">{s.name}</td>
                    <td className="px-2.5 py-1.5 text-muted-foreground">{s.session_title || t('training:course_only', 'Course assignment')}</td>
                    <td className="px-2.5 py-1.5">
                      <div className="text-foreground">{s.practice_title}</div>
                      <div className="text-[11px] text-muted-foreground">{verificationLabel(t, s.verification_kind)}</div>
                    </td>
                    <td className="px-2.5 py-1.5 text-muted-foreground whitespace-nowrap">
                      <span className={`mr-1.5 rounded-full px-2 py-0.5 text-[10px] font-medium ${SUBMISSION_STATUS_CLS[s.status] || ''}`}>{submissionStatusLabel(t, s.status)}</span>
                      {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {s.evidence_url ? <a className="inline-flex items-center gap-1 text-orange-600 hover:underline" href={s.evidence_url} target="_blank" rel="noreferrer">{t('training:view_evidence', 'View evidence')} <ExternalLink className="h-3 w-3" aria-hidden /></a> : <span className="text-muted-foreground">—</span>}
                      {s.score != null && <div className="text-[11px] text-muted-foreground">{t('training:score_n', 'Score: {{n}}', { n: s.score })}</div>}
                      {s.note && <div className="text-[11px] text-muted-foreground">{s.note}</div>}
                    </td>
                    <td className="px-2.5 py-1.5 text-right whitespace-nowrap">
                      {s.status === 'submitted' && decide?.id !== s.id && (
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="h-7" onClick={() => setDecide({ id: s.id, decision: 'validated', score: s.score != null ? String(s.score) : '', note: '' })} data-testid={`validate-${s.id}`}><Check className="mr-1 h-3.5 w-3.5" />{t('training:validate', 'Validate')}</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => setDecide({ id: s.id, decision: 'rejected', score: '', note: '' })} data-testid={`reject-${s.id}`}><X className="mr-1 h-3.5 w-3.5" />{t('training:reject', 'Reject')}</Button>
                        </div>
                      )}
                      {s.status !== 'submitted' && s.validation_note && <span className="text-[11px] text-muted-foreground">{s.validation_note}</span>}
                    </td>
                  </tr>
                  {decide?.id === s.id && (
                    <tr data-testid={`decide-${s.id}`}>
                      <td colSpan={6} className="px-2.5 py-2 bg-orange-50 dark:bg-orange-500/10">
                        <div className="flex flex-wrap items-end gap-2">
                          <div className="text-xs font-medium text-foreground">{decide.decision === 'validated' ? t('training:validate_title', 'Validate {{name}}', { name: s.name }) : t('training:reject_title', 'Reject {{name}}', { name: s.name })}</div>
                          <div className="space-y-1">
                            <Label htmlFor={`vq-score-${s.id}`} className="text-xs">{t('training:score_optional', 'Score (optional)')}</Label>
                            <Input id={`vq-score-${s.id}`} className="h-8 w-24" type="number" min={0} max={100} value={decide.score} onChange={(e) => setDecide({ ...decide, score: e.target.value })} />
                          </div>
                          <div className="space-y-1 flex-1 min-w-[10rem]">
                            <Label htmlFor={`vq-note-${s.id}`} className="text-xs">{t('training:note_optional', 'Note (optional)')}</Label>
                            <Input id={`vq-note-${s.id}`} className="h-8" value={decide.note} onChange={(e) => setDecide({ ...decide, note: e.target.value })} />
                          </div>
                          <Button size="sm" variant="ghost" className="h-8" onClick={() => setDecide(null)} disabled={validate.isPending}>{t('training:cancel', 'Cancel')}</Button>
                          <Button size="sm" className="h-8" variant={decide.decision === 'rejected' ? 'destructive' : 'default'} disabled={validate.isPending} onClick={send} data-testid={`confirm-${s.id}`}>
                            {validate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : decide.decision === 'validated' ? t('training:validate', 'Validate') : t('training:reject', 'Reject')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ValidationQueue;
